<template>
<div class="container-fluid public-view" style="height: 100%;">
    <div class="row" style="height: 100%;">
        <div class="public-side" v-bind:style="{ backgroundSize: 'cover', backgroundImage: `url('${backImg}')` }">
            <div class="public-logo">
                <img src="../assets/infinite-logo-white.svg" />
            </div>
        </div>
        <div class="public-main">
            <div id="main-tabs" class="scroll-y">
                <div style="display:none;">
                    <form id="passcode-poster" method="post" data-bind="attr: { 'action': passcodePath }">
                        <input type="hidden" id="passcode-return" name="returnUrl" />
                        <input type="hidden" name="sso" value="true" />
                        <input type="hidden" id="passcode-username" name="username" />
                    </form>
                </div>
                <div v-if="systemAnnouncements">
                    <div class="system-message-row" v-for="announcement in systemAnnouncements" :key="announcement.id">
                        <div :id="announcement.id" :class="announcement.css" v-html="announcement.message"></div>
                    </div>
                </div>
                <div class="tab-content">
                    <div id="tab1" class="tab-pane" v-bind:class="{ active: page == 'login' }">
                        <div class="panel">
                            <form ref="loginForm" @submit.prevent="login">
                                <div class="row">
                                    <div class="col-12 form-group">
                                        <h1 class="public-header">Hello again</h1>
                                        <div class="errortext" v-if="failed">{{ errorReason }}</div>
                                        <h2 class="public-subhead">Welcome to the Employer Portal</h2>
                                        <p class="public-description">Self-service account management at your fingertips</p>
                                    </div>
                                    <template v-if="!hideLogin">
                                        <div class="col-12 form-group">
                                            <label>Username</label>
                                            <input class="form-control input-sm" name="username" v-model="username" autofocus />
                                        </div>
                                        <div class="col-12 form-group">
                                            <label>Password</label>
                                            <input class="form-control input-sm" name="password" type="password" v-model="password" />
                                        </div>
                                        <div class="col-12 form-group">
                                            <button class="btn btn-public">
                                                Secure Sign In
                                            </button>
                                        </div>
                                        <div class="col-12 form-group">
                                            <ul class="public-other-links">
                                                <li>
                                                    Do you need to
                                                    <router-link to="/forgot-password">reset your password?</router-link>
                                                </li>
                                                <li>
                                                    First Time User?
                                                    <router-link to="/user-registration">Setup your account.</router-link>
                                                </li>
                                                <li>
                                                    Looking for another system?
                                                    <a class="other-portals-tooltip" href="https://www.epopdocs.com/" data-toggle="tooltip" title="" data-placement="bottom" target="_blank" data-original-title="Request new or duplicate POP documents, revise or renew existing documents, and access the online discrimination testing portal.">ePOP+</a>
                                                    | <a class="other-portals-tooltip" href="https://cobra.myameriflex.com/" data-toggle="tooltip" title="" data-placement="bottom" target="_blank" data-original-title="To manage COBRA information, view group participant and plan information, add participants, and run reports.">COBRAPoint</a>
                                                </li>
                                            </ul>
                                        </div>
                                    </template>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div id="tab2" class="tab-pane" v-bind:class="{ active: page == 'forgot-password' }">
                        <div class="panel">
                            <form method="post" @submit.prevent="verifyUsername">
                                <div class="row">
                                    <div class="col-12 form-group">
                                        <h1 class="public-header">Forgotten Password?</h1>
                                        <div class="errortext" v-if="failed">{{ errorReason }}</div>
                                        <p class="public-description">Please enter your username to reset your password.</p>
                                    </div>
                                    <div class="col-12 form-group">
                                        <label>Username</label>
                                        <input class="form-control input-sm" name="username" v-model="username" autofocus/>
                                    </div>
                                    <div class="col-12 form-group">
                                        <button class="btn btn-public">Send Reset Email</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div id="tab3" class="tab-pane" v-bind:class="{ active: page == 'reset-password' }">
                        <div class="panel">
                            <form method="post" @submit.prevent="resetPassword">
                                <password-form v-if="uuid && passcode"
                                    :isPasswordReset="true" :passcode="passcode" :uuid="uuid" :errorReason="errorReason"
                                        @submit="resetPassword" @link-expired="linkIsExpired" />
                            </form>
                        </div>
                    </div>
                    <div id="tab4" class="tab-pane" :class="{ active: page == 'password-expired' }">
                        <div class="panel">
                            <form method="post" @submit.prevent="verifyUsername">
                                <div class="row">
                                    <div class="col-12 form-group">
                                        <h1 class="public-header">Password Expired</h1>
                                        <p class="public-description" style="margin-bottom: 0;">
                                            Your password has expired and must be reset. Click the button below to receive your password reset e-mail
                                        </p>
                                    </div>
                                    <div class="col-12 form-group">
                                        <button class="btn btn-public">Send Reset Email</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import { config } from '../config'
import Axios from 'axios';
import sideBackPic from '../assets/background-forest-blue.jpg'
import GoogleAuth from './GoogleAuth'
import { appVersion } from  './utils/app-version'
import auth from '../auth'
import router from '../router'
import loginMixin from './login-mixin'
import PasswordForm from './PasswordForm.vue'

let pages = {
    login: "login",
    forgot: "forgot-password",
    reset: "reset-password"
};
let error_codes = {
    RESET_LIMIT: 'Your password can only be reset once per day',
    PASSWORD_REQUIREMENTS: 'Unable to reset your password. Passwords must contain one of each of the following: uppercase, lowercase, and numeric characters.',
    LOGIN_FAILED: 'Username or password is invalid. Please try again.',
    LOCKED_OUT: 'Your account has been locked due to too many invalid login attempts',
    CANNOT_REUSE: 'You cannot reuse this password',
    INVALID_PASSCODE: 'This link has expired and you must request a new one. Use the resend link below to start again'
}

function goToGoogleSignInPage(returnUrl) {
    let fullUrl = `${config.romeUi}/#/identity`;
    
    if(returnUrl) {
        returnUrl = encodeURIComponent(returnUrl);
        fullUrl = `${fullUrl}?returnUrl=${returnUrl}`;
    }

    window.location.href = fullUrl;
}

export default {
    mixins: [loginMixin],
    components: { PasswordForm },
    beforeCreate() {
        if(GoogleAuth.isGsuiteUser()) {
            var query = router.currentRoute.query || {};
            goToGoogleSignInPage(query.returnUrl);
        }
    },
    async created() {
        this.loadSystemAnnouncements();
        this.page = this.routePage;
        this.load();
    },
    computed: {
        ...amfx.map('session', 'loggedIn,error'),
        routePage(newValue) {
            return (this.$route.path || "/").slice(1);
        }
    },
    data() {
        return {
            page: "",
            backImg: sideBackPic,
            username: null,
            password: null,
            failed: false,
            passcodePath: `${config.romeApi}/authentication/password/reset`,
            confirmPassword: null,
            systemAnnouncements: null,
            hideLogin: false,
            errorReason: null,
            uuid: null
        }
    },
    watch: {
        routePage(newValue) {
            this.page = newValue || "login";
            this.requestFailed(false);
            this.load();
        },
    },
    methods: {
        requestFailed(reason) {
            if(reason === false) {
                this.failed = false;
                this.errorReason = null;
            }
            else {
                this.failed = true;
                this.errorReason = reason;
            }
        },
        async load() {
            if (this.page == 'reset-password') {
                let query = this.$route.query || {};
                if(!query.uuid || !query.otp) throw "Reset password requires a token";
                this.uuid = query.uuid || null;
                this.passcode = query.otp || null;
            }
        },
        async linkIsExpired() {
            this.page = 'forgot-password';
            this.requestFailed('This link has expired and you must request a new one.');
        },
        goToMfa(result, username, password) {
            var p = {
                phone: result.data.phone,
                email: result.data.email[0],
                uid: result.data.uid[0]
            }

            var data = { ...p, username, password }
            this.$router.push({ name: 'MfaChallenge', params: data });
        },
        async login() {
            if(!this.username || !this.password) {
                toastService.error('Please provide a username and password.');
                return false;
            }

            let needsUpdate = await appVersion.checkForUpdates();
            if(needsUpdate) {
                window.location.reload(true);
                return;
            }

            let result = await auth.credentials(this.username, this.password);

            if(result.authenticated) {
                this.requestFailed(false);
                await this.goToRecent();
            }
            else {
                let error = result.error;
                if(error == 'invalid_geo_location')
                    this.requestFailed("We're sorry, it appears that our website currently doesn't support your region. If you believe this is an error, please contact our customer support team for more information.");
                else if(error == 'password_expired')
                    this.page = 'password-expired';
                else if(error == 'locked_out')
                    this.requestFailed(error_codes.LOCKED_OUT);
                else if(error == 'mfa_lock') 
                    this.requestFailed('Your account is temporarily locked due to too many failed login attempts. Please try again later');
                else if(error == 'gsuite')
                    goToGoogleSignInPage(this.returnUrl);
                else if(error == 'mfa_required')
                    this.goToMfa(result, this.username, this.password);
                else
                    this.requestFailed(error_codes.LOGIN_FAILED);
            }

            return true;
        },
        async verifyUsername() {
            if(!this.username) {
                toastService.error('Please provide a valid username.');
                return false;
            }

            var failedToSendEmail = () => {
                this.requestFailed("Failed to send an email. Please try again.");
            }

            try {
                let api = Axios.create();
                let username = encodeURIComponent(this.username);
                let result = await api.get(this.passcodePath + "?username=" + username);

                let data = result.data;
                if(data && data.success) {
                    this.requestFailed(false);
                    toastService.success(
                        'An email has been sent. Please click the link inside to reset your password.'
                    );
                }
                else if(data && !data.success) {
                    let rsp = data.data;
                    var msg = error_codes[data.data];
                    if(msg) this.requestFailed(msg);
                    else failedToSendEmail();
                }
            }
            catch(e) { failedToSendEmail(); }
        },
        async resetPassword(args) {
            let api = Axios.create();
            let data = {
                UserId: this.uuid,
                Passcode: this.passcode,
                Password: args.password,
                PasswordRepeat: args.confirmPassword
            }
            
            try {
                let result = await api.post(this.passcodePath, data);

                if(result.data && result.data.success) {
                    this.username = result.data.username;
                    this.password = args.password;
                    this.login();
                    return true;
                }
                else if(result.data && result.data.success === false) {                    
                    if(result.data.errorReason == 'INVALID_PASSCODE') {
                        await this.linkIsExpired();
                    }
                    else if(error_codes[result.data.errorReason]) {
                        this.requestFailed(error_codes[result.data.errorReason]);
                    }
                    else {
                        this.requestFailed(error_codes.PASSWORD_REQUIREMENTS);
                    }
                }
            }
            catch(e) { this.requestFailed(error_codes.PASSWORD_REQUIREMENTS); }
            return false;
        },
        async loadSystemAnnouncements() {
            let api = Axios.create();
            let result = await api.get(`${config.romeApi}/announcement?clientName=`+ config.clientName);
            if(!(result.data && result.data.length > 0)) {
                return;
            }
            let filteredAnnouncements = result.data;
            this.systemAnnouncements = filteredAnnouncements.map(a => {
                if(!this.hideLogin && a.alertType === "danger"){
                    this.hideLogin = true;
                }
                return { id: a.id, message: a.message, css: 'alert alert-' + a.alertType }
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.system-message-row {
    display: flex;
    margin-left: auto;
    flex-wrap: wrap;
}
.alert {
    width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/framework/Login.vue